import { Box, FullScreenLoading, Notification } from '@bfa/common-ui';
import React, { useState } from 'react';
import ReactHtmlParser from 'react-html-parser';

import AcquisitionPageHead from '../../../../components/AcquisitionPageHead';
import { Divider } from '../../../../components/Divider';
import Error from '../../../../components/Error';
import ErrorBanner from '../../../../components/ErrorBanner';
import Layout from '../../../../components/Layout';
import { GQLLayoutContent, GQLProgram, Maybe } from '../../../../gql.generated';
import { FBPixelEventInfo } from '../../../../utils/facebookEvents/types';
import { GtmEvents, GtmEventsScriptProps } from '../../../../utils/gtmEvents';
import { getProgramDisplayTitle } from '../../../../utils/program';
import { CmsImage } from '../../../../utils/types';
import Content from '../../components/Content';
import Disclaimers from '../../components/Disclaimers';
import GbxUpgradeButton from '../../components/GbxUpgradeButton';
import Heroimage from '../../components/HeroImage';
import LearnMoreModal from '../../components/LearnMoreModal';
import useUpgradeToGBX from '../../hooks/useUpgradeGbxOneClick';
import {
  GbxOneClickMainContainer,
  TextMainContainer,
} from './gbxOneClick.styled';

export type GbxOneClickUpgradePageProps = {
  landingHeroMobileImg?: CmsImage;
  landingHeroDesktopImg?: CmsImage;
  landingHeader?: string;
  landingExplanatory?: string;
  landingValueProperty?: string;
  landingExplanation?: string;
  landingButtonCta?: string;
  landingChargeCopy?: string;
  checkoutSubscriptionDisclaimer?: string;
  checkoutShippingDisclaimer?: string;
  landingLearnMoreHeader?: string;
  landingLearnMoreExplanatory?: string;
  landingLearnMoreBulletPoints?: Array<string>;
  landingErrorMsg?: string;
  checkoutBannerText?: string;
  noThanksCta?: string;
  landingBulletsPoints?: Array<string>;
};

export type GbxOneClickUpgradeProps = {
  error: boolean;
  facebookEventsProps?: FBPixelEventInfo;
  fireSubscriptionPaymentSuccessPixel?: boolean;
  gbxOneClickUpgradePage?: GQLLayoutContent['gbxOneClickUpgradePage'];
  program?: GQLProgram;
  fromCheckout?: boolean;
  fromAddons?: boolean;
  fromUP?: boolean;
  noCheckout?: boolean;
  paymentRetryEnabled?: boolean;
  gtmEventsProps?: GtmEventsScriptProps;
  gbxMetaTitle?: string;
  gbxMetaDescription?: string;
  isNotEligible?: boolean;
  consent?: string;
  shippingCost?: string;
  isRebootRenameOn?: boolean;
};

const getBannerText = (
  fromAddons?: boolean,
  fromUP?: boolean,
  program?: GQLProgram,
  defaultText?: string | null,
  isRebootRenameOn?: boolean
) => {
  const programName = getProgramDisplayTitle(
    program === GQLProgram.FULLSIZE ? GQLProgram.FULLSIZE : GQLProgram.GLAMBAG,
    '',
    isRebootRenameOn
  );

  if (fromAddons) {
    return `You're scoring Add-Ons in your ${programName}!`;
  }
  if (fromUP) {
    return `You've customized your ${programName}`;
  }
  return defaultText;
};

const seeLearnMoreModal = (
  landingLearnMoreHeader?: Maybe<string> | undefined,
  landingLearnMoreExplanatory?: Maybe<string> | undefined,
  landingLearnMoreBulletPoints?: Maybe<Maybe<string>[]> | undefined
) => {
  return (
    (!!landingLearnMoreHeader ||
      !!landingLearnMoreExplanatory ||
      landingLearnMoreBulletPoints?.some((item) => !!item)) ??
    false
  );
};

const GbxOneClickUpgrade = ({
  error,
  fireSubscriptionPaymentSuccessPixel,
  facebookEventsProps,
  gbxOneClickUpgradePage,
  program,
  fromCheckout,
  fromAddons,
  fromUP,
  noCheckout,
  paymentRetryEnabled,
  gtmEventsProps,
  gbxMetaTitle,
  gbxMetaDescription,
  isNotEligible,
  consent,
  shippingCost,
  isRebootRenameOn,
}: GbxOneClickUpgradeProps): JSX.Element => {
  const {
    handleUpgradeToGbx,
    handleNoThanks,
    onCrossClick,
    onLogoClick,
    logAnalytics,
    toggleModal,
    showModal,
    onUpgradeError,
    loading,
  } = useUpgradeToGBX(
    fromCheckout ?? false,
    noCheckout ?? false,
    paymentRetryEnabled ?? false,
    isNotEligible || false,
    program,
    gtmEventsProps,
    fireSubscriptionPaymentSuccessPixel
  );
  if (error) return <Error />;
  if (loading) return <FullScreenLoading loading spinnerWidth={4.4} />;

  const {
    landingHeroMobileImg,
    landingHeroDesktopImg,
    landingExplanation,
    landingButtonCta,
    landingChargeCopy,
    checkoutSubscriptionDisclaimer,
    checkoutShippingDisclaimer,
    noThanksCta,
    landingLearnMoreHeader,
    landingLearnMoreExplanatory,
    landingLearnMoreBulletPoints,
    checkoutBannerText,
    landingErrorMsg,
  } = gbxOneClickUpgradePage || {};

  const shouldSeelearnMoreModal = seeLearnMoreModal(
    landingLearnMoreHeader,
    landingLearnMoreExplanatory,
    landingLearnMoreBulletPoints
  );

  const showBanner = ((fromAddons || fromUP) && !noCheckout) || fromCheckout;

  const bannerText = getBannerText(
    fromAddons,
    fromUP,
    program,
    checkoutBannerText,
    isRebootRenameOn
  );

  const formattedLandingExplanation = landingExplanation?.replace(
    '${shippingCost}',
    shippingCost ?? ''
  );

  return (
    <Layout
      header={{
        cross: !fromCheckout,
        minimum: true,
        onCrossClick,
        onLogoClick,
      }}
    >
      {gbxMetaTitle && (
        <AcquisitionPageHead
          title={gbxMetaTitle}
          description={gbxMetaDescription}
        />
      )}
      {gtmEventsProps && <GtmEvents {...gtmEventsProps} />}
      <LearnMoreModal
        header={landingLearnMoreHeader}
        explanatory={landingLearnMoreExplanatory}
        bulletPoints={landingLearnMoreBulletPoints}
        shouldSeelearnMoreModal
        {...{
          showModal,
          toggleModal,
          logAnalytics,
        }}
      />
      <GbxOneClickMainContainer>
        {showBanner && (
          <Notification
            noMarginBottom
            color="navy"
            icon="icon-ipsy-check-circle coral"
            type="success"
          >
            {bannerText}
          </Notification>
        )}
        <Heroimage
          landingHeroImg={[landingHeroMobileImg, landingHeroDesktopImg]}
        />
        <TextMainContainer marginTop="2rem" padding={['0 1.5rem', '0']}>
          <ErrorBanner errorMsg={landingErrorMsg} show={onUpgradeError} />

          <Content
            gbxOneClickUpgradePage={gbxOneClickUpgradePage}
            shouldSeelearnMoreModal={shouldSeelearnMoreModal}
            toggleModal={toggleModal}
          />
          {consent && (
            <Box pt={'2.4rem'} pb={'0.4rem'}>
              <Box textAlign={'left'}>{ReactHtmlParser(consent)}</Box>
            </Box>
          )}
          <GbxUpgradeButton
            onUpgradeSubmit={handleUpgradeToGbx}
            onNoThanks={handleNoThanks}
            {...{
              landingButtonCta,
              landingExplanation: formattedLandingExplanation,
              noThanksCta,
              landingChargeCopy,
              fromCheckout,
            }}
          />
          <Divider small marginTop={['2rem', '3.2rem']} />
          <Disclaimers
            disclaimers={[
              checkoutSubscriptionDisclaimer,
              checkoutShippingDisclaimer,
            ]}
          />
        </TextMainContainer>
      </GbxOneClickMainContainer>
    </Layout>
  );
};

export default GbxOneClickUpgrade;
